import { useEffect, useState, useSyncExternalStore } from 'react';

import type { ReactNode } from 'react';

import type { HubConnection, HubConnectionState } from '@microsoft/signalr';
import { LoginState } from '@treneo/shared/types';

import {
  connectSignalR,
  disconnectSignalR,
  getStateSnapshot,
  setRefreshTokenHandler,
  signalRConnection,
  subscribeStateChange
} from './signalr-connection';
import { SignalRConnectionContext } from './signalr-connection-context';

interface Props {
  children: ReactNode;
  loginState: LoginState;
  onRefreshToken: () => Promise<void>;
}

export const SignalRConnectionContextProvider = ({
  children,
  loginState,
  onRefreshToken
}: Props) => {
  const state = useSyncExternalStore<HubConnectionState>(subscribeStateChange, getStateSnapshot);
  const [value, setValue] = useState<{ connection: HubConnection; state: HubConnectionState }>({
    connection: signalRConnection,
    state
  });

  useEffect(() => {
    setValue({ connection: signalRConnection, state });
  }, [state]);

  useEffect(() => {
    setRefreshTokenHandler(onRefreshToken);
  }, [onRefreshToken]);

  useEffect(() => {
    if (loginState !== LoginState.LOGGED) {
      disconnectSignalR();
      return undefined;
    }
    const delayMs = 6000;
    const timeoutId = setTimeout(() => {
      connectSignalR({ skipAuthCheck: true });
    }, delayMs);
    return () => clearTimeout(timeoutId);
  }, [loginState]);

  return (
    <SignalRConnectionContext.Provider value={value}>{children}</SignalRConnectionContext.Provider>
  );
};
